/**
 * Home's "Since you left" (collaboration phase 4): for the person's most recent projects, what others changed since they
 * last left (vfs-service `GET /collab/since-last-seen`, per project, the person's own last visit), shown for the project
 * with the most news: "Since you left store-locator yesterday at 18:40 · 23 changes in 9 files: Luca 11, Flow chat for
 * Luca 9, Sara 3", with links to the project's changes by person and its Activity.
 *
 * `projects`: `[{ _id, name }]`, most recent first (at most `max` are asked). Nothing is shown when nothing changed, or
 * when vfs-service does not answer (an older one).
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getSinceLastSeen } from '../../services/CollabService';
import { formatRelativeTime } from '../../utils/relativeTime';
import { sinceSummary } from '../../utils/presenceModel';
import { AuthorMark } from './ChangesPanel';
import './Collab.css';

/** "yesterday at 18:40", "today at 09:12", "on 3 Oct at 18:40". */
export function leftAt(iso, now = new Date()) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) { return ''; }
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const day = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(at)) / 86400000);
  if (days === 0) { return `today at ${time}`; }
  if (days === 1) { return `yesterday at ${time}`; }
  return `on ${at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} at ${time}`;
}

/** The project with the most changes since the person left, from `[{ project, data }]`, or null. */
export function pickNews(answers) {
  return answers
    .filter((answer) => answer?.data?.changes > 0)
    .sort((a, b) => b.data.changes - a.data.changes)[0] || null;
}

export default function SinceYouLeft({ projects = [], you = null, onOpenProject, max = 3 }) {
  const [news, setNews] = useState(null);
  const ids = projects.slice(0, max).map((project) => project._id).filter(Boolean).join(',');

  useEffect(() => {
    let cancelled = false;
    setNews(null);
    const chosen = projects.slice(0, max).filter((project) => project._id);
    if (!chosen.length) { return undefined; }
    Promise.all(chosen.map((project) => getSinceLastSeen(project._id)
      .then((data) => ({ project, data }))
      .catch(() => null)))
      .then((answers) => { if (!cancelled) { setNews(pickNews(answers.filter(Boolean))); } });
    return () => { cancelled = true; };
    // `ids` stands for the projects asked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, max]);

  if (!news) { return null; }
  const { project, data } = news;
  const open = (history) => onOpenProject && onOpenProject(project._id, { history });
  return (
    <section aria-labelledby="since-you-left" className="sbx-card collab-since" data-testid="since-you-left">
      <div className="collab-since-head">
        <h2 id="since-you-left">Since you left {project.name || project._id}</h2>
        <span className="collab-muted collab-small" title={new Date(data.since).toLocaleString()}>{leftAt(data.since)} · {formatRelativeTime(data.since)}</span>
      </div>
      <p className="collab-since-summary">{sinceSummary(data, you)}</p>
      <div className="collab-since-people">
        {(data.authors || []).slice(0, 5).map((author) => (
          <AuthorMark author={author.author} key={author.key} size={22} via={author.via} />
        ))}
      </div>
      <div className="collab-since-actions">
        <button className="collab-link" data-testid="since-changes" onClick={() => open('person')} type="button">See changes by person</button>
        <button className="collab-link" data-testid="since-activity" onClick={() => open('activity')} type="button">Activity</button>
      </div>
    </section>
  );
}

SinceYouLeft.propTypes = {
  max: PropTypes.number,
  onOpenProject: PropTypes.func,
  projects: PropTypes.arrayOf(PropTypes.object),
  you: PropTypes.object,
};
